<template>
  <div class="dex-content">
    <h1 class="text-h4 mb-4">QuarnX DEX</h1>
    
    <v-card class="mb-6">
      <v-card-text>
        <div class="text-body-1 mb-2">
          Bem-vindo à Exchange Descentralizada (DEX) da QuarnX. Negocie tokens QRC20 de forma segura e sem intermediários diretamente na blockchain.
        </div>
        <v-alert
          color="warning"
          icon="mdi-information-outline"
          variant="tonal"
          density="compact"
        >
          A DEX está em fase beta e em breve estará disponível para operações reais. Por enquanto, você pode explorar a interface e as funcionalidades.
        </v-alert>
      </v-card-text>
    </v-card>
    
    <v-row>
      <!-- Gráfico de Preço -->
      <v-col cols="12" lg="8">
        <v-card height="400">
          <v-card-title class="d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-avatar size="28" color="primary" class="mr-2">
                <span class="text-h6 text-white">Q</span>
              </v-avatar>
              
              <v-select
                v-model="parAtual"
                :items="paresTradingDisponiveis"
                variant="plain"
                density="compact"
                hide-details
                class="trading-pair-select"
              ></v-select>
            </div>
            
            <div class="d-flex align-center">
              <v-btn-toggle
                v-model="intervaloTempo"
                mandatory
                density="comfortable"
                variant="outlined"
              >
                <v-btn value="1h">1h</v-btn>
                <v-btn value="1d">1d</v-btn>
                <v-btn value="1w">1w</v-btn>
                <v-btn value="1m">1m</v-btn>
              </v-btn-toggle>
            </div>
          </v-card-title>
          
          <v-divider></v-divider>
          
          <!-- Mockup de Gráfico (em produção seria um gráfico real com Chart.js) -->
          <div class="price-chart pa-4 d-flex flex-column justify-center align-center">
            <div class="text-h6 mb-2">{{ parAtual }}</div>
            <div class="text-h4 font-weight-bold mb-3 d-flex align-center">
              {{ precoPar }}
              <span 
                :class="[
                  'ml-2 text-body-2 font-weight-medium',
                  variacaoPositiva ? 'text-success' : 'text-error'
                ]"
              >
                {{ variacaoPositiva ? '+' : '' }}{{ variacaoPar }}%
              </span>
            </div>
            
            <!-- Simulação de gráfico de velas -->
            <div class="candlestick-chart">
              <div 
                v-for="i in 30" 
                :key="i" 
                class="candle"
                :class="{ 'candle-up': true }"
              >
                <div class="wick"></div>
                <div class="body"></div>
                <div class="wick bottom"></div>
              </div>
            </div>
            
            <div class="d-flex justify-space-between w-100 mt-2">
              <span class="text-caption">{{ horaInicio }}</span>
              <span class="text-caption">Agora</span>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <!-- Formulário de Trading -->
      <v-col cols="12" md="6" lg="4">
        <v-card>
          <v-tabs v-model="tabTrading">
            <v-tab value="comprar">{{ t('dex.buy') }}</v-tab>
            <v-tab value="vender">{{ t('dex.sell') }}</v-tab>
          </v-tabs>
          
          <v-card-text>
            <v-window v-model="tabTrading">
              <v-window-item value="comprar">
                <div class="py-2">
                  <div class="text-body-2 mb-1">Você compra</div>
                  <v-text-field
                    v-model="valorCompra"
                    type="number"
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="coin"
                  ></v-text-field>
                  
                  <div class="text-body-2 mb-1">Você recebe</div>
                  <v-text-field
                    :value="valorCompra / 2"
                    readonly
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="parAtual.split('/')[0]"
                  ></v-text-field>
                  
                  <div class="text-caption text-grey mb-4">
                    Taxa: 0.3% ({{ (valorCompra * 0.003).toFixed(5) }} {{ coin }})
                  </div>
                  
                  <v-btn
                    color="success"
                    block
                    :disabled="!valorCompra || valorCompra <= 0"
                  >
                    Comprar {{ parAtual.split('/')[0] }}
                  </v-btn>
                </div>
              </v-window-item>
              
              <v-window-item value="vender">
                <div class="py-2">
                  <div class="text-body-2 mb-1">Você vende</div>
                  <v-text-field
                    v-model="valorVenda"
                    type="number"
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="parAtual.split('/')[0]"
                  ></v-text-field>
                  
                  <div class="text-body-2 mb-1">Você recebe</div>
                  <v-text-field
                    :value="valorVenda * 2"
                    readonly
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="coin"
                  ></v-text-field>
                  
                  <div class="text-caption text-grey mb-4">
                    Taxa: 0.3% ({{ (valorVenda * 2 * 0.003).toFixed(5) }} {{ coin }})
                  </div>
                  
                  <v-btn
                    color="error"
                    block
                    :disabled="!valorVenda || valorVenda <= 0"
                  >
                    Vender {{ parAtual.split('/')[0] }}
                  </v-btn>
                </div>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
        
        <!-- Saldos -->
        <v-card class="mt-3">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-wallet-outline</v-icon>
            Seus Saldos
          </v-card-title>
          
          <v-list density="compact">
            <v-list-item>
              <template v-slot:prepend>
                <v-avatar size="24" color="primary">
                  <span class="text-white text-caption">Q</span>
                </v-avatar>
              </template>
              <v-list-item-title>{{ coin }}</v-list-item-title>
              <v-list-item-subtitle>QuarnX</v-list-item-subtitle>
              <template v-slot:append>
                <span class="font-weight-medium">1,000.00</span>
              </template>
            </v-list-item>
            
            <v-list-item>
              <template v-slot:prepend>
                <v-avatar size="24" color="green darken-2">
                  <span class="text-white text-caption">Q</span>
                </v-avatar>
              </template>
              <v-list-item-title>QSTB</v-list-item-title>
              <v-list-item-subtitle>QuarnX Stablecoin</v-list-item-subtitle>
              <template v-slot:append>
                <span class="font-weight-medium">500.00</span>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      
      <!-- Livro de Ordens -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-book-open-variant</v-icon>
            Livro de Ordens
          </v-card-title>
          
          <v-divider></v-divider>
          
          <div class="pa-2">
            <!-- Ordens de Venda -->
            <div class="d-flex justify-space-between text-caption text-grey mb-1 px-2">
              <span>Preço ({{ coin }})</span>
              <span>{{ t('dex.amount') }}</span>
              <span>{{ t('dex.total') }}</span>
            </div>
            
            <div class="sell-orders mb-3">
              <div 
                v-for="i in 5" 
                :key="'sell-'+i" 
                class="order-row d-flex justify-space-between py-1 px-2"
                :style="{backgroundColor: 'rgba(var(--v-theme-error), '+ (0.05 + i*0.02) +')'}"
              >
                <span class="text-error">{{ (0.27 + i*0.01).toFixed(2) }}</span>
                <span>{{ '--' }}</span>
                <span>{{ '--' }}</span>
              </div>
            </div>
            
            <!-- Preço Atual -->
            <div class="current-price d-flex justify-center py-2 mb-3 font-weight-bold" style="background-color: rgba(var(--v-theme-primary), 0.1);">
              <span>{{ precoPar }}</span>
            </div>
            
            <!-- Ordens de Compra -->
            <div class="buy-orders">
              <div 
                v-for="i in 5" 
                :key="'buy-'+i" 
                class="order-row d-flex justify-space-between py-1 px-2"
                :style="{backgroundColor: 'rgba(var(--v-theme-success), '+ (0.05 + i*0.02) +')'}"
              >
                <span class="text-success">{{ (0.23 - i*0.01).toFixed(2) }}</span>
                <span>{{ '--' }}</span>
                <span>{{ '--' }}</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <!-- Lista de Pares de Trading -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-currency-usd</v-icon>
            Pares de Trading
          </v-card-title>
          
          <v-divider></v-divider>
          
          <v-text-field
            v-model="busca"
            density="compact"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar pares"
            class="pa-2"
            hide-details
          ></v-text-field>
          
          <v-list density="compact">
            <v-list-subheader>Populares</v-list-subheader>
            <v-list-item
              v-for="par in paresFiltrados"
              :key="par.simbolo"
              :active="parAtual === par.simbolo"
              @click="parAtual = par.simbolo"
            >
              <v-list-item-title>{{ par.simbolo }}</v-list-item-title>
              <template v-slot:append>
                <div class="d-flex flex-column align-end">
                  <span :class="par.variacao >= 0 ? 'text-success' : 'text-error'">
                    {{ par.variacao >= 0 ? '+' : '' }}{{ par.variacao }}%
                  </span>
                  <span class="text-caption">Vol: {{ par.volume }}</span>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'DexContent',
  data() {
    const c = window.__QUARNX_COIN__ || 'tKinX'
    return {
      parAtual: `QTK/${c}`,
      paresTradingDisponiveis: [
        `QTK/${c}`,
        `QGLD/${c}`,
        `QSTB/${c}`,
        `QETH/${c}`
      ],
      intervaloTempo: '1d',
      tabTrading: 'comprar',
      valorCompra: 100,
      valorVenda: 10,
      busca: '',
      paresTrading: [
        { simbolo: `QTK/${c}`, preco: 0.25, variacao: 5.2, volume: '120K' },
        { simbolo: `QGLD/${c}`, preco: 2.35, variacao: 1.7, volume: '85K' },
        { simbolo: `QSTB/${c}`, preco: 1.00, variacao: 0.1, volume: '250K' },
        { simbolo: `QETH/${c}`, preco: 3.75, variacao: -2.3, volume: '95K' },
        { simbolo: `QDOT/${c}`, preco: 0.75, variacao: 3.8, volume: '65K' },
        { simbolo: `QBNB/${c}`, preco: 1.20, variacao: -1.5, volume: '78K' }
      ]
    }
  },
  computed: {
    precoPar() {
      return 0.25
    },
    variacaoPar() {
      return '0.00'
    },
    variacaoPositiva() {
      return false
    },
    horaInicio() {
      const agora = new Date()
      
      switch(this.intervaloTempo) {
        case '1h':
          return this.formatarHora(this.minutosAtras(60))
        case '1d':
          return this.formatarHora(this.minutosAtras(60 * 24))
        case '1w':
          return this.formatarHora(this.minutosAtras(60 * 24 * 7))
        case '1m':
          return this.formatarHora(this.minutosAtras(60 * 24 * 30))
        default:
          return this.formatarHora(this.minutosAtras(60))
      }
    },
    paresFiltrados() {
      if (!this.busca) return this.paresTrading
      
      const termoBusca = this.busca.toLowerCase()
      return this.paresTrading.filter(par => 
        par.simbolo.toLowerCase().includes(termoBusca)
      )
    }
  },
  methods: {
    minutosAtras(minutos) {
      const data = new Date()
      data.setMinutes(data.getMinutes() - minutos)
      return data
    },
    formatarHora(data) {
      return `${data.getHours().toString().padStart(2, '0')}:${data.getMinutes().toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.candlestick-chart {
  display: flex;
  align-items: flex-end;
  height: 150px;
  width: 100%;
  padding: 0 10px;
}

.candle {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 0 1px;
}

.candle .wick {
  width: 1px;
  height: 20px;
  background-color: rgba(var(--v-theme-on-surface), 0.6);
}

.candle .wick.bottom {
  height: 15px;
}

.candle .body {
  width: 6px;
  height: 35px;
  background-color: rgba(var(--v-theme-error), 0.7);
  border-radius: 1px;
}

.candle-up .body {
  background-color: rgba(var(--v-theme-success), 0.7);
}
</style>
